declare global {
  interface Window {
    dataLayer: any[];
  }
}

export {};

import { Injectable } from '@angular/core';
import { take, filter } from 'rxjs';
import { UserState } from 'src/app/state/user-state';
import { environment } from 'src/environments/environment';
import { select, Store } from '@ngrx/store';
import { selectUserUuid } from './../../state/reducer/user.reducer';

@Injectable({ providedIn: 'root' })
export class GtmService {
  //  PROD
  private readonly GTM_ID = 'GTM-PJGBW8G';
  // DEV
  // private readonly GTM_ID = 'GTM-WZJNDX7L';

  private isInitialized = false;
  private userUuid: string | undefined;

  constructor(private userStore: Store<UserState>) {
    this.userStore
      .pipe(
        select(selectUserUuid),
        filter((id): id is string => id !== null && id !== undefined),
        take(1)
      )
      .subscribe((userUuid) => {
        this.userUuid = userUuid;
      });
  }

  initializeGTM(): void {
    if (!environment.production || this.isInitialized) {
      return;
    }
    this.loadGoogleTagManager();
    this.addNoScriptFallback();
    this.isInitialized = true;
  }

  private loadGoogleTagManager(): void {
    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });

    // Create and append GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${this.GTM_ID}`;
    document.head.appendChild(script);
  }

  private addNoScriptFallback(): void {
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${this.GTM_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  pushPurchaseVoucherEvent(purchasedVoucher: any) {
    this.push({
      event: 'purchase',
      user_id: this.userUuid,
      ecommerce: {
        transaction_id: purchasedVoucher?.voucherGUID,
        currency: 'MBC',
        value: purchasedVoucher?.voucher.price,
        items: [
          {
            item_id: purchasedVoucher?.voucher_ID.toString(),
            item_name: purchasedVoucher?.voucher.name,
            item_category: purchasedVoucher?.voucher.voucherCategory.name,
            price: purchasedVoucher?.voucher.price,
            quantity: 1
          }
        ]
      }
    });
  }

  pushPurchaseProductVoucherEvent(purchasedVoucher: any) {
    this.push({
      event: 'purchase',
      user_id: this.userUuid,
      ecommerce: {
        transaction_id: purchasedVoucher?.voucherGUID,
        currency: 'RSD',
        value: purchasedVoucher?.voucher.value,
        items: [
          {
            item_id: purchasedVoucher?.voucher_ID.toString(),
            item_name: purchasedVoucher?.voucher.name,
            item_category: purchasedVoucher?.voucher.voucherCategory.name,
            price: purchasedVoucher?.voucher.price,
            quantity: 1
          }
        ]
      }
    });
  }

  pushViewVoucherEvent(voucher: any) {
    this.push({
      event: 'view_item',
      user_id: this.userUuid,
      ecommerce: {
        currency: 'RSD',
        value: voucher.value,
        items: [
          {
            item_id: voucher.voucher_ID.toString(),
            item_name: voucher.name,
            item_category: voucher.voucherCategoryName,
            price: voucher.price,
            quantity: 1
          }
        ]
      }
    });
  }

  push(event: any) {
    if (!environment.production) {
      console.log('GTM Event (dev mode):', event);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }
}
